import { WallThicknessStatus } from './WallThicknessReview.jsx';
import { WallHeightStatus } from './WallHeightReview.jsx';
import DeliveryReadiness from './DeliveryReadiness.jsx';
import { framingPenalty } from '../engine/plannerCandidates.js';
import LightingPane from './LightingPane.jsx';
import { ContextGuide } from './HelpGuide.jsx';
import SiteLayoutPane, { SiteCoverage } from './SiteLayoutPane.jsx';
import { siteArea } from '../engine/geometry.js';
import { areaEnvironment, environmentAt, outdoorReady } from '../engine/siteAreas.js';
import { planningSettings } from '../engine/planning.js';
import { priceLabel, totalLabel } from '../engine/costs.js';
import VerificationPanel, { RedundancyBreakdown } from './VerificationPanel.jsx';
import EditFields from './EditFields.jsx';
import { FieldMeasureMessage, StrictNumberInput, useFieldMeasure } from './FieldMeasureInput.jsx';
import StairsPane from './StairsPane.jsx';
import PeoplePane from './PeoplePane.jsx';
import { CameraDelivery } from './DeliveryPanels.jsx';
import { detailLevels } from '../engine/standards.js';
import { TasksPane, ObjectsPane, StandardsProfile, RemovalPanel, FloorsPane, TaskResults } from './PlanningPanels.jsx';
import { CameraContribution, PointInspection, InstallationPane, EntryCoverage } from './DesignPanels.jsx';
import { snapWallCamera } from '../engine/mounting.js';
import CameraMountOptions from './CameraMountOptions.jsx';
import { isCeilingCamera, isPoleCamera, cameraMountKind, mountingProfile, defaultMountKind } from '../engine/cameraMounts.js';
import { ceilingCameraPose } from '../engine/ceilingMounting.js';
import { ModelPicker } from './CatalogueBrowser.jsx';
import { lazy, Suspense, useCallback, useRef, useState } from 'react';
import { deviceDecimalSeparator, setDeviceDecimalSeparator } from '../engine/manualLength.js';
import Icon from './Icon.jsx';
import { usePlan } from '../store.js';
import CameraNightVision from './CameraNightVision.jsx';
import ReviewSummary from './ReviewSummary.jsx';
import { fieldToolsAvailable, openFieldTool } from '../engine/fieldToolBridge.js';
import { useProfessional } from '../engine/toolMode.js';
import { fmt, clamp, requirementIntent, REQS, LEVELS, reqByKey } from '../engine/constants.js';
import { camGeom, camRanges, groundBand, rangeFor, tiltFor } from '../engine/optics.js';
import { polyArea, polyCentroid } from '../engine/geometry.js';
import { billOfMaterials } from '../engine/coverage.js';
import { readPalette, hexA } from '../engine/render.js';
import { formatArea, formatDensity, formatLength, measurementLabel, measurementUnit } from '../engine/units.js';

// The five workflow steps come first, in order; specialist sections sit in a quieter second row.
const TABS = [
  { k: 'plan', t: 'Site', step: 1, hint: 'Set scale and draw the physical site' },
  { k: 'tasks', t: 'Needs', step: 2, hint: 'Mark entrances, capture paths and extra detail requirements' },
  { k: 'cams', t: 'Cameras', step: 3, hint: 'Choose devices and place or generate cameras' },
  { k: 'anal', t: 'Review', step: 4, hint: 'Find weak spots and verify the camera layout' },
  { k: 'deliver', t: 'Deliver', step: 5, hint: 'Review the report, then save and share your plan' },
  { k: 'lighting', t: 'Lighting' },
  { k: 'install', t: 'Install' },
  { k: 'setup', t: 'Standards' }
];
const CameraPreview = lazy(() => import('./CameraPreview.jsx'));

export default function Inspector({
  store, tab, setTab, readOnly, onCreateRoom, onDeliverAction,
  onCentre, onCalibrate, onUploadBg, onEditModel, onSyncModels, onBrowseModels, onLookThrough, themeRev, onDrawCable, onPlaceEquipment, onInspect, onPlaceLight, onShowLighting, onReviewLighting, lightingView, lightingOn,
  showGuidance, onHelp, onGuideAction, onPlaceTarget, onReviewWallHeight, onReviewFloorElevation
}) {
  usePlan(store);
  const wallHeightTriggerRef = useRef(null);
  const guide = topic => showGuidance && <ContextGuide {...{ topic, store, readOnly, onHelp }} onAction={onGuideAction} />;
  return (
    <EditFields store={store} className="inspector-fields">
      <div className="inspector-heading"><span><Icon name="layers" size={16} />Site inspector</span><button className="guide-link" aria-label={`Help for ${TABS.find(t => t.k === tab)?.t || 'Site'}`} onClick={() => onHelp(tab)}>Help</button><span className="count-badge">{readOnly ? 'View only' : 'Design'}</span></div>
      <div className="tabs workflow-tabs" role="tablist" aria-label="Inspector sections">
        {TABS.map(t => (
          <button key={t.k} role="tab" className={'tab' + (t.step ? ' step' : ' more') + (tab === t.k ? ' on' : '')} title={t.hint}
            id={'inspector-tab-' + t.k} aria-controls={'inspector-pane-' + t.k} tabIndex={tab === t.k ? 0 : -1}
            aria-selected={tab === t.k}
            onKeyDown={e => {
              const i = TABS.findIndex(x => x.k === tab);
              const next = e.key === 'ArrowRight' ? (i + 1) % TABS.length : e.key === 'ArrowLeft' ? (i + TABS.length - 1) % TABS.length : e.key === 'Home' ? 0 : e.key === 'End' ? TABS.length - 1 : -1;
              if (next < 0) return;
              e.preventDefault(); e.stopPropagation(); setTab(TABS[next].k);
              document.getElementById('inspector-tab-' + TABS[next].k)?.focus();
            }}
            onClick={() => setTab(t.k)}>{t.step && <span className="step-number" aria-hidden="true">{t.step}</span>}{t.t}</button>
        ))}
      </div>
      <div className="panes">
        <div role="tabpanel" id="inspector-pane-plan" aria-labelledby="inspector-tab-plan" className={'pane' + (tab === 'plan' ? ' on' : '')}>
          {guide('plan')}
          <SiteLayoutPane {...{ store, readOnly, onCreateRoom }} />
          <SitePane {...{ store, readOnly, onCentre, onCalibrate, onUploadBg, onReviewWallHeight, wallHeightTriggerRef }} />
          <FloorsPane {...{ store, readOnly, onReviewFloorElevation }} />
          <StairsPane {...{ store, readOnly, onCentre }} />
          <PeoplePane {...{ store, readOnly, onCentre, onLookThrough }} />
          <ObjectsPane {...{ store, readOnly, onCentre, onGuideAction }} />
        </div>
        <div role="tabpanel" id="inspector-pane-tasks" aria-labelledby="inspector-tab-tasks" className={'pane' + (tab === 'tasks' ? ' on' : '')}>{guide('tasks')}<TasksPane {...{ store, readOnly, onCentre }} /></div>
        <div role="tabpanel" id="inspector-pane-cams" aria-labelledby="inspector-tab-cams" className={'pane' + (tab === 'cams' ? ' on' : '')}>
          {guide('cams')}
          <CamerasPane {...{ store, readOnly, onCentre, onEditModel, onLookThrough }} />
        </div>
        <div role="tabpanel" id="inspector-pane-anal" aria-labelledby="inspector-tab-anal" className={'pane' + (tab === 'anal' ? ' on' : '')}>
          {guide('anal')}
          <ReviewSummary store={store} onStage={step => step === 'quotes' ? onDeliverAction('quotes') : setTab(step)} />
          <AnalysisPane {...{ store, onCentre, setTab, themeRev, onInspect, readOnly, onLookThrough }} />
        </div>
        <div role="tabpanel" id="inspector-pane-lighting" aria-labelledby="inspector-tab-lighting" className={'pane' + (tab === 'lighting' ? ' on' : '')}><LightingPane {...{ store, readOnly, onCentre, onLookThrough, onPlaceLight, onShowLighting, onReviewLighting, lightingView, lightingOn, onHelp, showGuidance, onPlaceTarget }} active={tab === 'lighting'} /></div>
        <div role="tabpanel" id="inspector-pane-install" aria-labelledby="inspector-tab-install" className={'pane' + (tab === 'install' ? ' on' : '')}>
          {guide('install')}
          <InstallationPane {...{ store, readOnly, onDrawCable, onPlaceEquipment, onCentre }} />
        </div>
<div role="tabpanel" id="inspector-pane-deliver" aria-labelledby="inspector-tab-deliver" className={'pane' + (tab === 'deliver' ? ' on' : '')}><DeliveryReadiness store={store} onNavigate={setTab} onAction={onDeliverAction} readOnly={readOnly} /></div>
        <div role="tabpanel" id="inspector-pane-setup" aria-labelledby="inspector-tab-setup" className={'pane' + (tab === 'setup' ? ' on' : '')}>
          {guide('setup')}
          <StandardsProfile {...{ store, readOnly }} />
          <StandardsPane {...{ store, readOnly, onEditModel, onSyncModels, onBrowseModels }} />
        </div>
      </div>
    </EditFields>
  );
}

/* ============================ SITE ============================ */

function ReferenceDrawingSummary({doc}) {
  const evidence=doc.bg?.referenceEvidence,source=evidence?.originalSource;
  if(evidence?.source!=='reference-drawing'||typeof source?.fileName!=='string')return null;
  const calibration=evidence.calibration,known=Number.isFinite(calibration?.metres)&&calibration.metres>0&&Number.isFinite(calibration?.spanPixels)&&calibration.spanPixels>=1;
  const originalScale=known?calibration.metres/calibration.spanPixels:null;
  const unchanged=known&&Math.abs(doc.bg.mpp-originalScale)<=Math.max(1e-12,Math.abs(originalScale)*1e-10);
  return <section aria-label="Reference drawing source" style={{overflowWrap:'anywhere'}}>
    <p className="hint">Imported from <strong>{source.fileName.slice(0,200)}</strong>{source.kind==='pdf-page'&&Number.isInteger(source.page)?` · page ${source.page}`:''}.</p>
    {known?<p className="hint">Import calibration used {formatLength(calibration.metres,doc,3)} between marked image points. {unchanged?'The current image scale matches that import calibration.':'The image scale has changed since import; the original calibration record is retained.'}</p>:<p className="hint">Imported with approximate scale. {doc.setup?.scaleConfirmed?'Scale was subsequently confirmed in the editor.':'Calibrate against a known distance before tracing.'}</p>}
  </section>;
}

function SitePane({ store, readOnly, onCentre, onCalibrate, onUploadBg, onReviewWallHeight, wallHeightTriggerRef }) {
  const S = store.doc, o = S.opt;
  const set = patch => store.setOpt(patch);
  const totalArea = siteArea(S);

  return (
    <>
      <section className="sect">
        <h3>Units &amp; scale</h3>
        <label>Measurement units<select aria-label="Measurement units" value={o.measurementSystem || 'metric'} disabled={readOnly} onChange={e => store.setMeasurementSystem(e.target.value)}><option value="metric">Metric · metres, m², lux</option><option value="imperial">Imperial · feet, ft², foot-candles</option></select></label>
        <DecimalStyle />
        <div className="field">
          <label htmlFor="in-scale">Drawing scale</label>
          <div className="ctl">
            <StrictNumberInput id="in-scale" doc={S} kind="scale" min={1} max={1000} value={o.scale} onChange={scale => set({ scale })} disabled={readOnly} />
            <span className="unit">{measurementUnit(S, 'scale')}</span>
          </div>
        </div>
        <button className="btn wide sm" onClick={onCalibrate} disabled={readOnly}>
          Calibrate from a known distance
        </button>
        <p className="hint">
          Avisera Planner stores geometry in metres for stable calculations and converts every editable measurement for this site.
          If you traced over a photo or PDF, calibrate against a known real-world distance in the units selected above.
        </p>
      </section>

      <section className="sect">
        <h3>Background</h3>
        <ReferenceDrawingSummary doc={S}/>
        <button className="btn wide sm" onClick={onUploadBg} disabled={readOnly}>
          {S.bg ? 'Replace floor plan image' : 'Upload floor plan image'}
        </button>
        {S.bg && (
          <>
            <div className="field" style={{ marginTop: 8 }}>
              <label htmlFor="bg-op">Opacity</label>
              <div className="ctl">
                <input id="bg-op" type="range" min="0" max="100" value={S.bg.op}
                  onChange={e => { S.bg.op = +e.target.value; store.touch({ reevaluate: false }); }}
                  disabled={readOnly} />
              </div>
            </div>
            <div className="field">
              <label htmlFor="bg-mpp">Image scale</label>
              <div className="ctl">
                <StrictNumberInput id="bg-mpp" doc={S} kind="resolution" min={0.0005} max={100} value={S.bg.mpp}
                  onChange={mpp => { if (mpp !== S.bg.mpp) S.setup = { ...S.setup, scaleConfirmed: false }; S.bg.mpp = mpp; store.touch({ reevaluate: false }); }}
                  disabled={readOnly} />
                <span className="unit">{measurementUnit(S, 'resolution')}</span>
              </div>
            </div>
            <label className="check">
              <input type="checkbox" checked={!!S.bg.locked} disabled={readOnly}
                onChange={e => { S.bg.locked = e.target.checked; store.touch({ reevaluate: false }); }} />
              <span>
                <span className="t">Lock position</span>
                <span className="d">Unlock to drag the image with the Select tool.</span>
              </span>
            </label>
            <button className="btn wide sm danger"
              onClick={() => { store.snapshot(); S.bg = null; store.touch(); }} disabled={readOnly}>
              Remove image
            </button>
          </>
        )}
        <p className="hint">
          Draw walls on top of it with the Wall tool — the traced walls, not the picture, are what
          the coverage engine sees. Images are stored with the project, not inside the plan.
        </p>
      </section>

      <section className="sect">
        <h3>Grid and plan labels</h3><label>Plan labels<select aria-label="Plan labels" value={store.viewLabels || 'auto'} onChange={e => { store.viewLabels = e.target.value; store.emit(); }}><option value="auto">Automatic · reduce clutter when zoomed out</option><option value="essential">Cameras and capture targets</option><option value="all">All labels</option><option value="selected">Selected item only</option></select></label>
        <div className="field">
          <label htmlFor="in-grid">Grid spacing</label>
          <div className="ctl">
            <StrictNumberInput id="in-grid" doc={S} kind="length" min={0.05} max={1000} value={o.grid} onChange={grid => set({ grid })} disabled={readOnly} />
            <span className="unit">{measurementUnit(S, 'length')}</span>
          </div>
        </div>
        <label className="check">
          <input type="checkbox" checked={o.snap} onChange={e => set({ snap: e.target.checked })} disabled={readOnly} />
          <span className="t">Snap to grid &amp; endpoints</span>
        </label>
        <label className="check">
          <input type="checkbox" checked={o.ortho} onChange={e => set({ ortho: e.target.checked })} disabled={readOnly} />
          <span className="t">Constrain drawing to 90° / 45°
            <span className="d">Hold Shift while clicking to draw free angles.</span>
          </span>
        </label>
        <label className="check">
          <input type="checkbox" checked={o.showGrid} onChange={e => set({ showGrid: e.target.checked })} />
          <span className="t">Show grid</span>
        </label>
      </section>

      <section className="sect">
        <h3>Areas <span className="num" style={{ color: 'var(--text-3)', fontSize: 10 }}>
          {S.areas.length ? formatArea(Math.max(0, totalArea), S) : ''}</span></h3>
        <div className="list">
          {S.areas.length === 0 ? (
            <div className="empty">No site outlined yet.<br />Pick the <b>Site area</b> tool and trace the space you want covered.</div>
          ) : S.areas.map(a => (
            <div key={a.id} className={'row-item' + (store.sel?.id === a.id ? ' on' : '')}
              onClick={() => { store.select({ type: 'area', id: a.id }); const c = polyCentroid(a.pts); onCentre(c.x, c.y); }}>
              <span className="dot" style={{ background: a.kind === 'floor' ? 'var(--cv-area-line)' : 'var(--cv-exclude-line)' }} />
              <button type="button" className="main">
                <span className="t">{a.name}</span>
                <span className="s">{a.kind === 'floor' ? areaEnvironment(a) : 'exclusion'} · {formatArea(polyArea(a.pts), S)} · {a.pts.length} pts</span>
              </button>
              {!readOnly && <button className="x" title="Delete" aria-label={`Delete ${a.name}`}
                onClick={e => { e.stopPropagation(); store.remove({ type: 'area', id: a.id }); }}><Icon name="close" size={12} /></button>}
            </div>
          ))}
        </div>
      </section>

      <section className="sect">
        <h3>Requirement zones <span className="num" style={{ color: 'var(--text-3)', fontSize: 10 }}>
          {S.zones.length || ''}</span></h3>
        <div className="list">
          {S.zones.length === 0 ? (
            <div className="empty">No requirement zones.<br />Draw one over a door, till or gate to hold that spot to a standard.</div>
          ) : S.zones.map(z => {
            const zr = store.cov?.zones.find(q => q.z.id === z.id);
            return (
              <div key={z.id} className={'row-item' + (store.sel?.id === z.id ? ' on' : '')}
                onClick={() => { store.select({ type: 'zone', id: z.id }); const c = polyCentroid(z.pts); onCentre(c.x, c.y); }}>
                <span className="dot" style={{ background: 'var(--cv-zone)' }} />
                <button type="button" className="main">
                  <span className="t">{z.name}</span>
                  <span className="s">{reqByKey(z.req).k} · {formatArea(polyArea(z.pts), S)}</span>
                </button>
                {zr && <span className={'pill ' + (zr.pct >= 99.5 ? 'pass' : zr.pct >= 60 ? 'warn' : 'fail')}>
                  {fmt(zr.pct, 0)}%
                </span>}
                {!readOnly && <button className="x" title="Delete" aria-label={`Delete ${z.name}`}
                  onClick={e => { e.stopPropagation(); store.remove({ type: 'zone', id: z.id }); }}><Icon name="close" size={12} /></button>}
              </div>
            );
          })}
        </div>
        <p className="hint">
          A zone tells the planner what a piece of floor has to deliver — an identifiable face at the
          front door, a readable plate at the gate. Outside a zone, each area uses its chosen requirement or the general
          target on the Standards tab.
        </p>
      </section>

      {store.sel && !['cam', 'area', 'stair'].includes(store.sel.type) && (
        <section className="sect">
          <h3>Selection</h3>
          <SelectionBody store={store} readOnly={readOnly} onReviewWallHeight={onReviewWallHeight} wallHeightTriggerRef={wallHeightTriggerRef} />
        </section>
      )}
    </>
  );
}

function SelectionBody({ store, readOnly, onReviewWallHeight, wallHeightTriggerRef }) {
  const sel = store.sel;
  const o = store.selected();
  const S = store.doc;
  const professional = useProfessional();
  if (!o) return null;

  if (sel.type === 'wall') {
    const len = Math.hypot(o.x2 - o.x1, o.y2 - o.y1);
    return (
      <>
        <div className="field">
          <label>Type</label>
          <div className="ctl">
            <select value={o.kind} disabled={readOnly}
              onChange={e => { store.snapshot(); o.kind = e.target.value; store.touch(); }}>
              <option value="solid">Solid — blocks sight</option>
              <option value="half">Low / counter — blocks below its height</option>
              <option value="glass">Glazing — see-through</option>
            </select>
          </div>
        </div>
        <WallHeightStatus wall={o} doc={S} readOnly={readOnly || !!store.sampleSource} onReview={onReviewWallHeight} triggerRef={wallHeightTriggerRef} />
        {o.kind === 'half' && <p className="hint">Choosing Low / counter keeps this wall’s current height. Review wall height separately if the top should be lower.</p>}
        <div className="field"><label htmlFor="wall-mount-rule">Mounting policy</label><div className="ctl"><select id="wall-mount-rule" value={o.mountRule || 'allowed'} disabled={readOnly}
          onChange={e => { store.snapshot(); o.mountRule = e.target.value; store.touch(); }}>
          <option value="allowed">Allowed</option><option value="preferred">Preferred</option><option value="prohibited">Prohibited</option>
        </select></div></div>
        <p className="hint">Applies to this drawn wall segment. Use separate wall segments for different mounting sections. Prohibited sections still block sight; preferred sections receive a small planning preference.</p>
        <dl className="readout"><dt>Length</dt><dd>{formatLength(len, S, 2)}</dd></dl>
        {!readOnly && fieldToolsAvailable() && <div className="grid2 selection-actions" role="group" aria-label="Wall actions">{[['measure', 'Measure this wall'], ...(professional ? [['thickness', 'Change thickness'], ['split', 'Split this wall'], ['pose', 'Move or rotate this wall']] : [])].map(([tool, label]) => <button key={tool} className="btn sm" onClick={() => openFieldTool(tool)}>{label}</button>)}</div>}
        <WallThicknessStatus wall={o} doc={S} readOnly={readOnly}/>
        {(S.wallLengthLocks||[]).some(lock=>lock.state==='active'&&lock.wallId===o.id)&&<p className="hint"><strong>Length locked.</strong> Move or rotate this wall while keeping its length. Use Field tools → Wall length locks to unlock it before resizing or deleting it.</p>}
        {!readOnly && <button className="btn wide sm danger" style={{ marginTop: 8 }}
          onClick={() => store.remove()}>Delete wall</button>}
      </>
    );
  }

  if (sel.type === 'area') {
    return (
      <>
        <div className="field">
          <label>Name</label>
          <div className="ctl">
            <input type="text" value={o.name} disabled={readOnly}
              onChange={e => { o.name = e.target.value; store.touch({ reevaluate: false }); }} />
          </div>
        </div>
        <div className="field">
          <label>Role</label>
          <div className="ctl">
            <select value={o.kind} disabled={readOnly}
              onChange={e => { store.snapshot(); o.kind = e.target.value; store.touch(); }}>
              <option value="floor">Site — cover this</option>
              <option value="exclude">Exclusion — ignore this</option>
            </select>
          </div>
        </div>

        <dl className="readout">
          <dt>Area</dt><dd>{formatArea(polyArea(o.pts), S)}</dd>
          <dt>Vertices</dt><dd>{o.pts.length}</dd>
        </dl>
        {!readOnly && <button className="btn wide sm danger" style={{ marginTop: 8 }}
          onClick={() => store.remove()}>Delete area</button>}
      </>
    );
  }

  if (sel.type === 'zone') {
    const zr = store.cov?.zones.find(q => q.z.id === o.id);
    return (
      <>
        <div className="field">
          <label>Name</label>
          <div className="ctl">
            <input type="text" value={o.name} disabled={readOnly}
              onChange={e => { o.name = e.target.value; store.touch({ reevaluate: false }); }} />
          </div>
        </div>
        <div className="field">
          <label>Must deliver</label>
          <div className="ctl">
            <select value={o.req} disabled={readOnly}
              onChange={e => { store.snapshot(); o.req = e.target.value; store.touch(); }}>
              {REQS.map(r => <option key={r.k} value={r.k}>{requirementIntent(r)}</option>)}
            </select>
          </div>
        </div>
        {o.req === 'plate' && (
          <div className="field">
            <label>Traffic runs</label>
            <div className="ctl">
              <input type="range" min="0" max="359" value={o.axis || 0} disabled={readOnly}
                onChange={e => { o.axis = +e.target.value; store.touch(); }} />
              <span className="unit num">{o.axis || 0}°</span>
            </div>
          </div>
        )}
        <div className="field">
          <label>Priority</label>
          <div className="ctl">
            <select value={o.priority || 2} disabled={readOnly}
              onChange={e => { o.priority = +e.target.value; store.touch({ reevaluate: false }); }}>
              <option value="3">Critical</option>
              <option value="2">High</option>
              <option value="1">Normal</option>
            </select>
          </div>
        </div>
        <div className="field"><label>Required views</label><div className="ctl"><select aria-label="Zone required views" value={o.views || 1} disabled={readOnly} onChange={e => { store.snapshot(); o.views = +e.target.value; store.touch(); }}><option value="1">One view</option><option value="2">Two views</option><option value="3">Three views</option></select></div></div>
        <dl className="readout">
          <dt>Area</dt><dd>{formatArea(polyArea(o.pts), S)}</dd>
          {zr && <><dt>Meets requirement</dt><dd>{fmt(zr.pct, 1)} %</dd></>}
          {zr && <><dt>Worst pixel density</dt><dd>{zr.total ? formatDensity(zr.minPx, S) : '—'}</dd></>}
          {zr && <><dt>Two-camera cover</dt><dd>{zr.total ? fmt(zr.red / zr.total * 100, 0) + ' %' : '—'}</dd></>}
        </dl>
        {!readOnly && <button className="btn wide sm danger" style={{ marginTop: 8 }}
          onClick={() => store.remove()}>Delete zone</button>}
      </>
    );
  }

  if (sel.type === 'bg') {
    return <p className="hint" style={{ margin: 0 }}>
      Background image selected. Drag it into place, then use <b>Set scale</b> to size it correctly.
    </p>;
  }
  return null;
}

/* ============================ CAMERAS ============================ */

function CamerasPane({ store, readOnly, onCentre, onEditModel, onLookThrough }) {
  const S = store.doc;
  const newModelId = store.model(store.newModelId)?.id || store.availableModels[0]?.id;
  const newModel = store.model(newModelId);
  const g = newModel ? camGeom({ ...S, models: [newModel] }, { modelId: newModel.id, focal: newModel.focal }) : null;
  const t = S.opt.t;
  const { total, unknown } = billOfMaterials(S);

  return (
    <>
      {store.sel?.type === 'cam' && (
        <CameraSettings store={store} readOnly={readOnly} onLookThrough={onLookThrough} />
      )}
      {!readOnly && (
        <details className="sect" open={store.sel?.type !== 'cam'}><summary>Add a camera</summary>
          <label className="check"><input type="checkbox" checked={store.snapCameras} onChange={e => { store.snapCameras = e.target.checked; store.emit(); }} /><span>Snap cameras to allowed walls</span></label>
          <ModelPicker store={store} id="new-model" value={newModelId} onChange={id => { store.newModelId = id; store.emit(); }} />
          <CameraMountOptions store={store} />
          <div className="grid2">
            <button className="btn sm" onClick={() => store.setTool('camera')}>Place on plan</button>
            <button className="btn sm" onClick={() => onEditModel(null)}>New model…</button>
          </div>
          {g && (
            <p className="hint">
              {fmt(g.hfov, 0)}° horizontal · geometric face-detail threshold at <b>{formatLength(rangeFor(g, t.identify), S)}</b>,
              recognition-detail threshold {formatLength(rangeFor(g, t.recognise), S)}, plate-detail threshold {formatLength(rangeFor(g, t.plate), S)}.
            </p>
          )}
        </details>
      )}

      <section className="sect">
        <h3>Placed <span className="num" style={{ color: 'var(--text-3)', fontSize: 10 }}>
          {S.cams.length ? `${S.cams.length} · ${totalLabel(total, unknown, S.opt.currency)}` : ''}</span></h3>
        <div className="list">
          {S.cams.length === 0 ? (
            <div className="empty">No cameras yet.<br />Place them by hand, or let <b>Auto-plan</b> propose a layout.</div>
          ) : S.cams.map((c, i) => {
            const cg = camGeom(S, c);
            const dead = store.cov && store.cov.camHits[i] === 0;
            return (
              <div key={c.id} role="group" onKeyDown={e => { if (e.target === e.currentTarget && ['Enter', ' '].includes(e.key)) { e.preventDefault(); e.stopPropagation(); store.select({ type: 'cam', id: c.id }); onCentre(c.x, c.y); } }} className={'row-item' + (store.sel?.id === c.id ? ' on' : '')}
                onClick={() => { store.select({ type: 'cam', id: c.id }); onCentre(c.x, c.y); }}>
                <span className="dot" style={{ background: dead ? 'var(--lv0)' : 'var(--lv4)' }} />
                <button type="button" className="main">
                  <span className="t">{c.name}{c.locked ? ' · locked' : ''}</span>
                  <span className="s">{cg?.m.name} · {cg ? fmt(cg.hfov, 0) : '?'}° · {formatLength(c.h, S)}</span>
                </button>
                {!readOnly && <button className="x" title="Delete" aria-label={`Delete ${c.name}`}
                  onClick={e => { e.stopPropagation(); store.remove({ type: 'cam', id: c.id }); }}><Icon name="close" size={12} /></button>}
              </div>
            );
          })}
        </div>
      </section>

    </>
  );
}

/** Camera X/Y/height in the Site display unit; malformed or out-of-range text
 * stays in the field instead of moving the camera to zero. Messages span the row. */
function CameraPositionFields({ doc, camera, locked, heightLocked, onChange }) {
  const fields = [
    ['camera-x', 'X', useFieldMeasure({ doc, value: camera.x, min: -100000, max: 100000, onChange: v => onChange({ x: v }) }), locked],
    ['camera-y', 'Y', useFieldMeasure({ doc, value: camera.y, min: -100000, max: 100000, onChange: v => onChange({ y: v }) }), locked],
    ['camera-height', 'Height', useFieldMeasure({ doc, value: camera.h, min: 0.3, max: 1000, onChange: v => onChange({ h: v }) }), heightLocked],
  ];
  return <>
    <div className="grid3">{fields.map(([id, axis, field, disabled]) => <div key={id}>
      <label className="unit" htmlFor={id}>{measurementLabel(axis, doc, 'length')}</label>
      <input id={id} disabled={disabled} {...field.input} />
    </div>)}</div>
    {fields.map(([id, , field]) => <FieldMeasureMessage key={id} field={field} />)}
  </>;
}

function CameraSettings({ store, readOnly, onLookThrough }) {
  const S = store.doc;
  const [preview, setPreview] = useState(false);
  // Fields scrolled into view (focus, error reveal, heading shortcuts) must stop
  // below the sticky camera heading, whose height changes as its name/buttons wrap.
  const headingObserver = useRef(null);
  const headingRef = useCallback(heading => {
    headingObserver.current?.disconnect(); headingObserver.current = null;
    if (!heading) return;
    const update = () => heading.parentElement?.style.setProperty('--selected-camera-heading-offset',
      Math.max(0, heading.offsetHeight + (parseFloat(getComputedStyle(heading).top) || 0)) + 'px');
    update();
    if (typeof ResizeObserver === 'function') { headingObserver.current = new ResizeObserver(update); headingObserver.current.observe(heading); }
  }, []);
  const c = store.selected();
  if (!c) return null;
  const g = camGeom(S, c);
  if (!g) return null;
  const m = g.m, rr = camRanges(S, c);
  const exterior = environmentAt(S, c.x, c.y, c.h) === 'outdoor';
  const bF = groundBand(c, g, S.opt.hFace);
  const i = S.cams.indexOf(c);
  const seen = store.cov ? store.cov.camHits[i] * store.cov.cell : null;
  const touch = () => store.touch();

  return (
    <section className="sect selected-camera-settings">
      <div className="selected-camera-heading" ref={headingRef}><h3>{c.name}</h3><nav aria-label="Selected camera properties"><button className="btn sm" onClick={() => document.getElementById('camera-name')?.focus()}>Basics</button><button className="btn sm" onClick={() => document.getElementById('camera-pan')?.focus()}>Aim &amp; view</button><button className="btn sm" onClick={() => { const d = document.getElementById('camera-checks'); if (d) { d.open = true; d.scrollIntoView({ block: 'nearest' }); } }}>Checks</button><button className="btn sm" onClick={() => { const d = document.getElementById('camera-installation'); if (d) { d.open = true; d.scrollIntoView({ block: 'nearest' }); } }}>Installation</button></nav></div>
      <button className="btn sm wide" aria-expanded={preview} onClick={() => setPreview(v => !v)}>{preview ? 'Hide camera preview' : 'Show camera preview'}</button>
      {preview && <Suspense fallback={<p>Loading camera preview…</p>}><CameraPreview store={store} cameraId={c.id} readOnly={readOnly} /></Suspense>}
      <label className="check"><input type="checkbox" aria-label="Lock this camera" checked={!!c.locked} disabled={readOnly}
        onChange={e => { store.snapshot(); c.locked = e.target.checked; store.touch(); }} /><span>Lock this camera</span></label>
      {c.locked && <p className="hint">Position, aim and model are protected from editing and replanning. Unlock to change or remove it.</p>}
      {!readOnly && !isCeilingCamera(S, c) && !isPoleCamera(S, c) && <button className="btn wide sm" disabled={c.locked} onClick={() => {
        const mount = snapWallCamera(S, c.x, c.y, c.h, 100000, c);
        if (mount) { store.snapshot(); c.x = mount.x; c.y = mount.y; c.pan = mount.pan; touch(); }
      }}>Snap to nearest allowed wall</button>}
      <div className="field">
        <label htmlFor="camera-name">Name</label>
        <div className="ctl">
          <input id="camera-name" type="text" value={c.name} disabled={readOnly || c.locked}
            onChange={e => { c.name = e.target.value; store.touch({ reevaluate: false }); }} />
        </div>
      </div>
      <ModelPicker store={store} id="placed-model" value={c.modelId} disabled={readOnly || c.locked} onChange={id => {
        store.snapshot();
        const mm = store.useModel(id);
        if (!mm) return;
        c.modelId = id;
        if (!mountingProfile(mm).kinds.includes(cameraMountKind(mm, c))) { c.mountKind = defaultMountKind(mm); c.mountSurface = ''; }
        if (isCeilingCamera(S, c)) { const pose = ceilingCameraPose(S, c); if (pose) Object.assign(c, pose); }
        c.focal = clamp(c.focal, mm.fmin, mm.fmax);
        if (mm.pano >= 360) c.tilt = 90;
        touch();
      }} />
      <CameraMountOptions key={'mount:' + c.id} store={store} camera={c} model={m} disabled={readOnly} />
      {!!m.details?.opticalProfiles?.length && <div className="field"><label htmlFor="optical-profile">Optical / output profile</label><div className="ctl"><select id="optical-profile" value={c.profileId || g.profileId || ''} disabled={readOnly || c.locked} onChange={e => { store.snapshot(); c.profileId = e.target.value; const p = m.details.opticalProfiles.find(p => p.id === c.profileId); if (p) c.focal = p.focal; touch(); }}><option value="">Model default</option>{m.details.opticalProfiles.map(p => <option key={p.id} value={p.id}>{p.name || p.id} · {p.hfov}°×{p.vfov}° · {p.resW}×{p.resH}</option>)}</select></div></div>}
      {m.details?.corridor && <label className="check"><input type="checkbox" checked={!!c.corridor} disabled={readOnly || c.locked} onChange={e => { store.snapshot(); c.corridor = e.target.checked; touch(); }} />Corridor orientation</label>}
      <p className="hint">{g.source}</p>
      {m.fmin !== m.fmax && m.details?.opticsMode !== 'published' && !c.profileId && (
        <div className="field">
          <label htmlFor="camera-focal">Focal length</label>
          <div className="ctl">
            <input id="camera-focal" type="range" min={m.fmin} max={m.fmax} step="0.1" value={c.focal} disabled={readOnly || c.locked}
              onChange={e => { c.focal = +e.target.value; touch(); }} />
            <span className="unit num">{fmt(c.focal, 1)} mm</span>
          </div>
        </div>
      )}
      <CameraPositionFields key={c.id} doc={S} camera={c} locked={readOnly || c.locked || isPoleCamera(S, c)}
        heightLocked={readOnly || c.locked || isCeilingCamera(S, c) || isPoleCamera(S, c)} onChange={patch => { Object.assign(c, patch); touch(); }} />
      {!!S.elevation && <p className="hint">Height is measured above {S.floorName || 'this level'}. Mounting elevation: {formatLength(S.elevation + c.h, S, 2)}.</p>}
      {exterior && <p className="hint" role="status">Exterior mount · {outdoorReady(m) ? 'outdoor-rated model' : 'outdoor suitability not confirmed; choose an outdoor-rated model'}.{!!S.elevation && ' Use Whole building to assess coverage on the grounds and other levels.'}</p>}
      <div className="field" style={{ marginTop: 8 }}>
        <label htmlFor="camera-pan">Pan</label>
        <div className="ctl">
          <input id="camera-pan" type="range" min="0" max="359" value={Math.round(c.pan)} disabled={readOnly || c.locked}
            onChange={e => { c.pan = +e.target.value; touch(); }} />
          <span className="unit num">{Math.round(c.pan)}°</span>
        </div>
      </div>
      <div className="field">
        <label htmlFor="camera-tilt">Tilt down</label>
        <div className="ctl">
          <input id="camera-tilt" type="range" min="0" max="90" value={Math.round(c.tilt)} disabled={readOnly || c.locked}
            onChange={e => { c.tilt = +e.target.value; touch(); }} />
          <span className="unit num">{Math.round(c.tilt)}°</span>
        </div>
      </div>
      {!readOnly && (
        <div className="grid2" style={{ margin: '8px 0' }}>
          <button className="btn sm" disabled={c.locked} onClick={() => {
            store.snapshot();
            c.tilt = tiltFor(c.h, S.opt.hFace, Math.min(camRanges(S, c).recognise, 12));
            touch();
          }}>Auto-tilt</button>
          <button className="btn sm" onClick={() => store.duplicateCam()}>Duplicate</button>
        </div>
      )}

      {onLookThrough && (
        <button className="btn wide sm" style={{ marginBottom: 8 }}
          onClick={() => onLookThrough(c.id)}>
          <Icon name="eye" />
          Look through this camera
        </button>
      )}

      <details className="sect" style={{ marginTop: 12 }}><summary>Estimated detail ranges</summary><p className="hint">Unobstructed geometric estimates. Lighting, motion and recorded image quality need separate checks.</p>
        <dl className="readout">
          <dt>Field of view</dt><dd>{fmt(g.hfov, 1)}° × {fmt(g.vfov, 1)}°</dd>
          <dt>Face-detail threshold</dt><dd>{formatLength(rr.identify, S)}</dd>
          <dt>Recognise to</dt><dd>{formatLength(rr.recognise, S)}</dd>
          <dt>Observe to</dt><dd>{formatLength(rr.observe, S)}</dd>
          <dt>Detect to</dt><dd>{formatLength(rr.detect, S)}</dd>
          <dt>Plate-detail threshold</dt><dd>{formatLength(rr.plate, S)}</dd>
          <dt>Blind under camera</dt><dd>{formatLength(bF.near, S, 2)}</dd>
          <dt>Vertical cut-off</dt><dd>{Number.isFinite(bF.far) ? formatLength(bF.far, S) : 'horizon'}</dd>
          <dt>Scene width at {formatLength(5, S)}</dt><dd>{formatLength(5 * g.spread, S, 2)}</dd>
          {seen !== null && <><dt>Floor it sees</dt><dd>{formatArea(seen, S)}</dd></>}
          <dt>Unit cost</dt><dd>{priceLabel(m, S.opt.currency)}</dd>
        </dl>
        {c.tilt > S.opt.aFace && (
          <p className="hint" style={{ color: 'var(--warn-ink)' }}>
            Tilted {Math.round(c.tilt)}° down — steeper than the {S.opt.aFace}° face limit, so anything
            directly below it is a cap, not a face.
          </p>
        )}
      </details>
      <details className="camera-subsection" id="camera-checks"><summary>Coverage contribution and removal check</summary><CameraContribution store={store} camera={c} /><RemovalPanel store={store} camera={c} readOnly={readOnly} /></details>
      <details className="camera-subsection" id="camera-installation"><summary>Recording and installation</summary><CameraDelivery key={c.id} store={store} camera={c} readOnly={readOnly} /></details>
      <details className="camera-subsection"><summary>Day / night vision</summary><CameraNightVision key={c.id} store={store} camera={c} readOnly={readOnly} /></details>
      {!readOnly && <button className="btn wide sm danger" disabled={c.locked} onClick={() => store.remove()}>Delete camera</button>}
    </section>
  );
}

/* ============================ ANALYSIS ============================ */

function AnalysisPane({ store, onCentre, setTab, themeRev, onInspect, readOnly, onLookThrough }) {
  const R = store.cov;
  const S = store.doc;
  const PAL = readPalette();

  if (!R) {
    return (
      <>
        <LiveToggle store={store} />
      <PointInspection {...{ store, onInspect, onCentre }} />
        <div className="empty">Outline a site area and place at least one camera, then the evaluation appears here.</div>
      </>
    );
  }

  const st = R.stats, bl = st.byLevel;
  const pct = i => (st.live ? bl[i] / st.live * 100 : 0);
  const { lines, total, unknown } = billOfMaterials(S);

  return (
    <>
      <SiteCoverage areas={R.areas} environments={R.environments} target={planningSettings(S).target} doc={S} />
      <LiveToggle store={store} />
      <PointInspection {...{ store, onInspect, onCentre }} />

      <section className="sect">
        <h3>Camera framing review</h3>
        <p className="muted">Coverage checks measure geometry. Inspect the recorded view for excess ceiling, walls and useful target framing before delivery.</p>
        {S.cams.filter(c => framingPenalty(S, c) >= 0.5).map(c => <div className="finding warn" key={c.id}><div><b>{c.name}</b><p>Possible excess ceiling or obstruction in the centre of the image.</p><button className="btn small" onClick={() => onLookThrough(c.id)}>Review camera view</button><button className="btn small" onClick={() => { store.select({ type: 'cam', id: c.id }); setTab('cams'); onCentre(c.x, c.y); }}>Adjust aim</button></div></div>)}
        {!S.cams.some(c => framingPenalty(S, c) >= 0.5) && <p className="muted">No automatic framing flags. Recorded image quality still needs a visual check.</p>}
      </section>
      <section className="sect">
        <h3>What to fix</h3>
        {R.findings.length === 0 ? <div className="empty">Nothing to flag.</div> : R.findings.map((f, i) => (
          <div key={i} role={f.go ? 'button' : undefined} tabIndex={f.go ? 0 : undefined} onKeyDown={e => { if (e.target === e.currentTarget && ['Enter', ' '].includes(e.key)) { e.preventDefault(); e.stopPropagation(); e.currentTarget.click(); } }} className={'finding ' + f.sev} onClick={() => { if (f.go) { store.probe = { x: f.go.x, y: f.go.y, req: f.go.req, heading: f.go.heading }; store.emit(); onCentre(f.go.x, f.go.y); } }}>
            <div><div className="t">{f.t}</div><div className="d">{f.d}</div></div>
          </div>
        ))}
      </section>
      <EntryCoverage entries={R.entries} doc={S} onInspect={p => { store.probe = { x: p.x, y: p.y, heading: p.heading, req: 'identify' }; store.emit(); onInspect(); onCentre(p.x, p.y); }} />
      <TaskResults S={S} results={R.tasks} onLocate={onCentre} />
      <section className="sect">
        <h3>Scorecard</h3>
        <div className="tiles">
          <div className="tile"><div className="k">Meets its target</div><div className="v">{fmt(st.metPct, 1)}%</div>
            <div className="d">of {formatArea(st.area, S, 0)} of site</div></div>
          <div className="tile"><div className="k">Blind floor</div><div className="v">{formatArea(st.blindArea, S)}</div>
            <div className="d">across {R.blind.length} pocket{R.blind.length === 1 ? '' : 's'}</div></div>
          <div className="tile"><div className="k">Face-detail grade</div>
            <div className="v">{fmt(st.faceArea / Math.max(st.area, 0.01) * 100, 0)}%</div>
            <div className="d">{formatArea(st.faceArea, S, 0)} at identify grade</div></div>
          <div className="tile"><div className="k">Two-camera</div><div className="v">{fmt(st.redundantPct, 0)}%</div>
            <div className="d">has a second view</div></div>
        </div>
      </section>

      <section className="sect">
        <h3>Grade by area</h3>
        <div className="stack">
          {detailLevels(S).map((_, i) => i).reverse().map(i => (
            <div key={i} title={detailLevels(S)[i].label}
              style={{ width: pct(i) + '%', background: i === 0 ? hexA(PAL.lv[0], 0.6) : PAL.lv[i] }} />
          ))}
        </div>
        <div className="stackkey">
          {detailLevels(S).map((_, i) => i).reverse().map(i => (
            <span className="i" key={i}>
              <span className="sw" style={{ background: i === 0 ? hexA(PAL.lv[0], 0.75) : PAL.lv[i] }} />
              {detailLevels(S)[i].label} <span className="n">{fmt(pct(i), 1)}%</span>
            </span>
          ))}
        </div>
      </section>

      <section className="sect">
        <h3>Requirement zones</h3>
        <div className="scroll-x" tabIndex={0} role="region" aria-label="Table, scrolls sideways">
          <table className="tbl">
            <thead><tr>
              <th>Zone</th><th>Needs</th>
              <th style={{ textAlign: 'right' }}>{measurementUnit(S, 'area')}</th>
              <th style={{ textAlign: 'right' }}>worst {measurementUnit(S, 'density')}</th><th />
            </tr></thead>
            <tbody>
              {R.zones.length === 0 ? (
                <tr><td colSpan={5} style={{ color: 'var(--text-3)' }}>No requirement zones defined.</td></tr>
              ) : R.zones.map(z => (
                <tr key={z.z.id} style={{ cursor: 'pointer' }}
                  onClick={() => { store.select({ type: 'zone', id: z.z.id }); const c = polyCentroid(z.z.pts); onCentre(c.x, c.y); setTab('plan'); }}>
                  <td>{z.z.name}</td>
                  <td>{reqByKey(z.z.req).k}</td>
                  <td className="n">{formatArea(z.area, S, 1, { unit: false })}</td>
                  <td className="n">{z.total ? formatDensity(z.minPx, S, 0, { unit: false }) : '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <span className={'pill ' + (z.pct >= 99.5 ? 'pass' : z.pct >= 60 ? 'warn' : 'fail')}>
                      {fmt(z.pct, 0)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <RedundancyBreakdown R={R} />
      <VerificationPanel store={store} readOnly={readOnly} />

      <section className="sect">
        <h3>Bill of materials</h3>
        <table className="tbl">
          <thead><tr><th>Model</th>
            <th style={{ textAlign: 'right' }}>Qty</th>
            <th style={{ textAlign: 'right' }}>Cost</th></tr></thead>
          <tbody>
            {lines.length === 0 ? (
              <tr><td colSpan={3} style={{ color: 'var(--text-3)' }}>No cameras.</td></tr>
            ) : lines.map(l => (
              <tr key={l.m.id}>
                <td>{l.m.name}</td>
                <td className="n">{l.n}</td>
                <td className="n">{priceLabel(l.m, S.opt.currency, l.n)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr>
            <td style={{ fontWeight: 600, color: 'var(--text)' }}>Total</td>
            <td className="n">{S.cams.length}</td>
            <td className="n" style={{ color: 'var(--accent-ink)' }}>{totalLabel(total, unknown, S.opt.currency)}</td>
          </tr></tfoot>
        </table>
        <p className="hint">Hardware only — no recorder, storage, cabling, licences or labour.</p>
      </section>
    </>
  );
}

function LiveToggle({ store }) {
  return (
    <section className="sect">
      <label className="check">
        <input type="checkbox" checked={store.live}
          onChange={e => { store.live = e.target.checked; if (store.live) store.evaluate(); else { store.cancelEvaluation(); store.touch({ dirty: false, reevaluate: false }); } }} />
        <span>
          <span className="t">Re-evaluate as I edit</span>
          <span className="d">Turn off on very large sites, then use Evaluate.</span>
        </span>
      </label>
      <button className="btn wide" disabled={store.busy} onClick={() => store.evaluate()}>{store.busy ? 'Evaluating coverage…' : 'Evaluate coverage'}</button>
      {store.coverageError && <p className="formerror" role="alert">{store.coverageError}</p>}
    </section>
  );
}

/* ============================ STANDARDS ============================ */

function StandardsPane({ store, readOnly, onEditModel, onSyncModels, onBrowseModels }) {
  const S = store.doc, o = S.opt;
  const num = (k, v) => store.setOpt({ [k]: v });
  const thr = (k, v) => store.setThreshold({ [k]: v });

  const T = [
    ['detect', 'Detect', o.t.detect],
    ['observe', 'Observe', o.t.observe],
    ['recognise', 'Recognise', o.t.recognise],
    ['identify', 'Identify (face)', o.t.identify],
    ['plate', 'Read plate', o.t.plate]
  ];
  const G = [
    ['hFace', 'Face height', o.hFace, 'length', 0.05],
    ['hPlate', 'Plate height', o.hPlate, 'length', 0.05],
    ['aFace', 'Max face angle', o.aFace, 'angle', 1],
    ['aPlateT', 'Max plate tilt', o.aPlateT, 'angle', 1],
    ['aPlateI', 'Max plate yaw', o.aPlateI, 'angle', 1]
  ];

  return (
    <>
      <section className="sect">
        <h3>Pixel density targets</h3>
        <p className="hint" style={{ marginTop: 0 }}>
          DORI thresholds in {measurementUnit(S, 'density')} of scene, measured on the target — the numbers a
          specifier actually signs off against. Change them if your local standard differs.
        </p>
        {T.map(([k, label, v]) => (
          <div className="field" key={k}>
            <label htmlFor={'t-' + k}>{label}</label>
            <div className="ctl">
              <StrictNumberInput id={'t-' + k} doc={S} kind="density" min={1} max={1e6} value={v} disabled={readOnly} onChange={value => thr(k, value)} />
              <span className="unit">{measurementUnit(S, 'density')}</span>
            </div>
          </div>
        ))}
      </section>

      <section className="sect">
        <h3>Capture geometry</h3>
        {G.map(([k, label, v, unit, step]) => (
          <div className="field" key={k}>
            <label htmlFor={'g-' + k}>{label}</label>
            <div className="ctl">
              <StrictNumberInput id={'g-' + k} doc={S} kind={unit === 'length' ? 'length' : 'number'} min={unit === 'length' ? 0.05 : 1} max={unit === 'length' ? 10 : 89} value={v} disabled={readOnly} onChange={value => num(k, value)} />
              <span className="unit">{unit === 'length' ? measurementUnit(S, 'length') : 'deg'}</span>
            </div>
          </div>
        ))}
        <p className="hint">
          Pixels alone don't get you a face. Look down on someone from 45° and you photograph the top
          of a cap; the angle limits are what stop the planner from claiming an identify it can't
          deliver. The same applies to plates, which also need the camera roughly square to the
          direction of travel.
        </p>
      </section>

      <section className="sect">
        <h3>Evaluation</h3>
        <div className="field">
          <label htmlFor="in-step">Sample spacing</label>
          <div className="ctl">
            <StrictNumberInput id="in-step" doc={S} kind="length" min={0.15} max={2} value={o.step} disabled={readOnly} onChange={value => num('step', value)} />
            <span className="unit">{measurementUnit(S, 'length')}</span>
          </div>
        </div>
        {!o.generalReq && <div className="field">
          <label htmlFor="in-general">General target</label>
          <div className="ctl">
            <select id="in-general" value={o.general} disabled={readOnly}
              onChange={e => store.setOpt({ general: +e.target.value, generalReq: '' })}>
              <option value="1">Detect</option><option value="2">Observe</option>
              <option value="3">Recognise</option><option value="4">Identify</option>
            </select>
          </div>
        </div>
        }
        <div className="field">
          <label htmlFor="in-cur">Currency</label>
          <div className="ctl">
            <input id="in-cur" type="text" maxLength={3} value={o.currency} disabled={readOnly}
              onChange={e => num('currency', e.target.value || '$')} />
          </div>
        </div>
      </section>

      <section className="sect">
        <h3>Camera library</h3>
        {!readOnly && <button className="btn wide sm" onClick={onBrowseModels}>Manage site library</button>}
        <div className="list">
          {store.availableModels.slice(0, 50).map(m => {
            const g = camGeom({ ...S, models: [m] }, { modelId: m.id, focal: m.focal });
            return (
              <div key={m.id} className="row-item" onClick={() => !readOnly && onEditModel(m.id)}>
                <button type="button" className="main">
                  <span className="t">{m.name}{m.isStock ? '' : ' ·'}</span>
                  <span className="s">{m.resW}×{m.resH} · {g ? fmt(g.hfov, 0) : '?'}° · {priceLabel(m, o.currency)}</span>
                </button>
                {m.isStock && <span className="pill mute">stock</span>}
              </div>
            );
          })}
        </div>
        {!readOnly && (
          <div className="grid2" style={{ marginTop: 6 }}>
            <button className="btn sm" onClick={() => onEditModel(null)}>Add a model</button>
            <button className="btn sm" onClick={onSyncModels}>Review library updates</button>
          </div>
        )}
        <p className="hint">
          This plan keeps its own snapshot of every model it uses, so an old revision still costs and
          evaluates the way it did when it was signed off. <b>Refresh from library</b> pulls the
          current specs from your account library into this plan.
        </p>
      </section>
    </>
  );
}

/** Typed lengths use one decimal mark on this device; it is a device setting,
 * so it applies in every Site and is not saved with the plan. */
function DecimalStyle() {
  const [value, setValue] = useState(deviceDecimalSeparator);
  return <label>Number format on this device<select aria-label="Number format on this device" value={value} onChange={e => { setDeviceDecimalSeparator(e.target.value); setValue(e.target.value); }}>
    <option value=".">Decimal point · 2.5 m</option><option value=",">Decimal comma · 2,5 m</option></select></label>;
}
